/**
 * Primitives d'interface (ARCHITECTURE.md §3, `components/ui/`).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 CE FICHIER EST UN ADAPTATEUR, PLUS UNE BIBLIOTHÈQUE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Depuis la migration complète sur la refonte « Nourri » *(26/08/2026)*, ces
 * primitives **ne décrivent plus aucun style** : elles délèguent à
 * `components/nourri/`. Ce qu'elles gardent, c'est leur **signature** — et
 * c'est tout l'intérêt. Trente fichiers les emploient ; les réécrire tous
 * aurait été trente occasions de casser un comportement pour un gain visuel
 * nul, alors que la même bascule se joue ici, en un endroit.
 *
 * ⚠️ **Ne pas écrire de neuf contre ce fichier.** Un écran nouveau importe
 * `components/nourri/` directement. Celui-ci n'existe que pour les surfaces
 * héritées, et il disparaîtra quand elles auront toutes été relues une à une.
 *
 * ⚠️ **`Sheet` fait exception** : ce n'est pas un adaptateur mais le composant
 * réel, parce que la refonte n'en fournit pas. Il porte le portail, le geste de
 * fermeture et le masquage de la barre d'onglets — trois mécaniques éprouvées
 * qu'on ne réécrit pas pour changer une couleur. Seule son apparence a été
 * reprise (`nourri-screens.css`, section 8).
 *
 * Deux contraintes tenues depuis l'étape 5, toujours vérifiées au banc :
 *   - cibles tactiles ≥ 44 px partout, champs de saisie ≥ 16 px ;
 *   - aucun `backdrop-filter`, nulle part (règle n°8).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { Button as NourriButton, Card as NourriCard, TextField } from '../nourri';

/* ==========================================================================
 * Texte
 * ======================================================================= */

/**
 * Texte secondaire.
 *
 * ⚠️ Porte une **classe**, jamais une `opacity`. Une opacité écrite à la main
 * ne peut pas convenir à deux thèmes : `opacity: 0.6` donnait 6,2:1 en sombre
 * et **4,0:1 en clair**, sous le seuil. C'est le jeton `--ink-soft` qui porte
 * la valeur, mesurée une fois.
 */
export function Hint({ children }: { children: ReactNode }) {
  return <p className="n-hint">{children}</p>;
}

/** Le serveur a répondu et refuse — ça ne se répare pas en attendant. */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="n-note n-note--error">
      {children}
    </p>
  );
}

/**
 * Jumeau **neutre** d'`ErrorNote` : quelque chose est en attente, rien n'est
 * cassé (`ARCHITECTURE_V2.md` §3.5).
 *
 * 🔴 **Ni rouge, ni `role="alert"`, et c'est le fond du sujet.** En V2 le hors
 * ligne est un mode *nominal* : un bandeau d'alerte sur une saisie qui s'est
 * parfaitement déroulée serait un mensonge, et c'est nommément ce que le §3.5
 * proscrit — « ne jamais présenter "hors ligne" comme une erreur ». `role`
 * vaut donc `status` : l'information est annoncée, sans interrompre.
 *
 * Elle reste **visible** pour autant : ne pas alerter n'est pas se taire. Une
 * saisie non confirmée doit se voir, sans quoi on la croit enregistrée.
 */
export function PendingNote({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="n-note">
      {children}
    </p>
  );
}

/* ==========================================================================
 * Contrôles
 * ======================================================================= */

/**
 * Bouton — adaptateur vers `nourri/Button`.
 *
 * | Ici                | Refonte                            |
 * |--------------------|------------------------------------|
 * | `variant="primary"`| `variant="primary" shape="rounded"` |
 * | `variant="quiet"`  | `variant="secondary" shape="rounded"`|
 * | `variant="danger"` | `variant="danger" shape="rounded"` |
 *
 * ⚠️ **`rounded` et non `pill`** : ces boutons-là vivent dans des formulaires
 * et des feuilles, aux côtés de champs à `--radius-md`. La pilule est la forme
 * des contrôles de navigation — segments, puces, plages — pas celle d'un
 * « Enregistrer » posé sous un champ.
 *
 * ⚠️ `style` reste accepté et passe tel quel : une trentaine d'appelants
 * ajustent un rembourrage ou une largeur. Ce n'est pas de la couleur, et le
 * jour où l'un d'eux en remet, c'est ce fichier qui doit le refuser.
 *
 * `confirming` joue la micro-animation de validation (`useConfirm`) : c'est le
 * retour qui remplace l'haptique sur iPhone, où l'API Vibration n'existe pas
 * (cf. `lib/haptics.ts`).
 */
export function Button({
  children,
  onClick,
  disabled,
  variant = 'primary',
  type = 'button',
  style,
  ariaLabel,
  confirming = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'quiet' | 'danger';
  type?: 'button' | 'submit';
  style?: CSSProperties;
  ariaLabel?: string;
  confirming?: boolean;
}) {
  return (
    <NourriButton
      type={type}
      variant={variant === 'quiet' ? 'secondary' : variant}
      shape="rounded"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={confirming ? 'is-confirming' : undefined}
      style={style}
    >
      {children}
    </NourriButton>
  );
}

/**
 * Champ texte — adaptateur vers `nourri/TextField`.
 *
 * ⚠️ L'`onChange` rend une **chaîne**, pas un événement : c'est la signature
 * qu'attendent les appelants, et la changer aurait été toucher aux trente
 * fichiers que cet adaptateur existe pour épargner.
 */
export function Field({
  label,
  value,
  onChange,
  placeholder,
  autoComplete = 'off',
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      // Toujours à la suite d'un geste explicite (ouverture d'une feuille),
      // jamais au chargement d'un écran.
      autoFocus={autoFocus}
    />
  );
}

/**
 * Champ numérique du chemin critique — adaptateur vers `nourri/TextField`.
 *
 * `inputMode="decimal"` fait apparaître le pavé numérique iOS au lieu du clavier
 * complet : sur les quatre champs d'une saisie libre, c'est la différence entre
 * tenir la cible des 15 secondes (specs §4) et la manquer. La valeur reste une
 * *chaîne* — on doit pouvoir vider le champ ou taper « 12. » sans que l'état se
 * batte avec la frappe ; la conversion se fait à la validation.
 *
 * ⚠️ Le `suffix` devient l'**unité** du champ de la refonte — « g », « kcal »
 * posés en queue plutôt qu'entre parenthèses dans le libellé. Même information,
 * à l'endroit où on la lit.
 */
export function NumberField({
  label,
  value,
  onChange,
  suffix,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  placeholder?: string;
}) {
  return (
    <TextField
      label={label}
      unit={suffix}
      numeric
      inputMode="decimal"
      value={value}
      onChange={(event) => {
        // Virgule décimale : c'est ce que produit le pavé numérique français.
        onChange(event.target.value.replace(',', '.'));
      }}
      placeholder={placeholder ?? '0'}
      autoComplete="off"
    />
  );
}

/* ==========================================================================
 * Surfaces
 * ======================================================================= */

/** Carte de contenu — adaptateur vers `nourri/Card`. */
export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <NourriCard style={style}>{children}</NourriCard>;
}

/** Course au-delà de laquelle le relâchement ferme la feuille. */
const SHEET_DISMISS_PX = 110;

/**
 * Nombre de feuilles ouvertes.
 *
 * Un compteur et non un booléen : deux feuilles peuvent se chevaucher le temps
 * d'une transition (on ferme l'une en ouvrant l'autre), et la première à se
 * démonter ne doit pas rendre la barre d'onglets à la seconde.
 */
let openSheets = 0;

/**
 * 🔴 Marque le document tant qu'une feuille est ouverte, pour **retirer la
 * barre d'onglets et l'en-tête collant** (cf. `surfaces.css`).
 *
 * ⚠️ **Sa cause d'origine a disparu, l'effet reste voulu.** C'était un
 * contournement de moteur : Safari iOS peignait une surface portant
 * `backdrop-filter` par-dessus des éléments pourtant plus hauts, si bien que la
 * barre d'onglets restait visible sur la feuille d'ajout. Le verre étant déposé
 * (règle n°8), l'ordre déclaré est de nouveau respecté — mais une barre
 * d'onglets **cliquable** derrière une feuille permettrait de changer d'onglet
 * en la laissant ouverte, ce qui n'a aucun sens.
 *
 * ⚠️ `visibility: hidden` et non `display: none` (côté CSS) : l'en-tête est dans
 * le flux défilant, et le faire disparaître ferait remonter le contenu derrière
 * le voile — donc sauter au moment de refermer.
 */
function useHideChromeWhileOpen(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    openSheets += 1;
    document.body.dataset.sheetOpen = 'true';
    return () => {
      openSheets -= 1;
      if (openSheets <= 0) {
        openSheets = 0;
        delete document.body.dataset.sheetOpen;
      }
    };
  }, [open]);
}

/**
 * Feuille modale, **fermable par glissement vers le bas**.
 *
 * 🔴 **Le seul composant de ce fichier qui n'est pas un adaptateur** : la
 * refonte n'en fournit pas d'équivalent, et il porte trois mécaniques éprouvées
 * — le portail, le geste, le masquage de la barre — qu'on ne réécrit pas pour
 * changer une couleur. Son apparence suit le `.sheet` du prototype
 * (`nourri-screens.css`, section 8).
 *
 * ⚠️ Le glissement ne part que de la **poignée et de l'en-tête**, jamais du
 * corps de la feuille — ni des **contrôles** que l'en-tête contient (cf.
 * `startDrag`). C'est délibéré : le corps défile (formulaire long, éditeur de
 * modèle), et capter le geste dessus rendrait le défilement imprévisible — on
 * tirerait la feuille en croyant faire défiler. La poignée est l'affordance iOS
 * habituelle, et l'en-tête offre une cible large.
 */

/**
 * Le tirage qui **prolonge le défilement** — demandé après usage *(06/09/2026)* :
 * « quand je scrolle vers le haut et que j'atteins le haut de la fenêtre, si je
 * continue de scroller, la fenêtre flottante se ferme dans le geste du scroll ».
 *
 * 🔴 **C'est un seul geste, pas deux.** La feuille se fermait déjà en glissant,
 * mais seulement depuis la **poignée** ou l'**en-tête** : arrivé en haut du
 * contenu, il fallait lever le doigt, viser la poignée, et recommencer. C'est
 * le geste des feuilles d'iOS qui manquait, et il ne se rattrape pas en
 * agrandissant la zone de préhension — ce qu'on veut, c'est que le tirage
 * *continue* le défilement.
 *
 * ⚠️ **`overscroll-behavior: none` est la condition, pas un détail.** Le
 * panneau portait `contain`, qui empêche bien le défilement de se propager à la
 * page derrière, mais **laisse le rebond local** : au sommet, iOS étirait la
 * feuille pendant qu'on essayait de la tirer, et les deux se disputaient le
 * doigt. `none` supprime ce rebond et laisse le sur-défilement entièrement à
 * ce code.
 *
 * ⚠️ **Écouteur natif et non passif** : React attache les siens en passif, donc
 * un `onTouchMove` de JSX ne pourrait pas appeler `preventDefault()` — et sans
 * lui le navigateur reprendrait la main dès que le doigt repart vers le bas.
 *
 * 🔴 **Le nœud est un ÉTAT, jamais une référence** : l'effet doit se rejouer
 * quand le panneau apparaît. Avec une référence, il ne tournerait qu'au montage
 * et l'écouteur ne serait jamais posé — le défaut attrapé le même jour sur
 * `useChartSweep`.
 */
/**
 * Le même verrou d'axe que partout ailleurs dans le projet — cf. `useDayPager`,
 * `useSwipeToDelete` et `useChartSweep`.
 */
const SHEET_PULL_LOCK_PX = 10;

function useSheetPull(
  panel: HTMLDivElement | null,
  onPull: (dy: number) => void,
  onRelease: () => void,
) {
  const geste = useRef<{ id: number; x: number; y: number; tient: boolean } | null>(null);

  useEffect(() => {
    if (panel === null) return;

    /**
     * 🔴 **Le garde-fou du défilement IMBRIQUÉ, et il n'est pas théorique.**
     * La roue de répétitions (`RepsWheel`, 05/09) est un défileur à
     * l'intérieur d'une feuille. Sans ce test, tourner la roue vers le bas
     * alors que la feuille est déjà en haut **fermerait la feuille** au milieu
     * du geste — on choisirait 8 répétitions et l'écran se refermerait.
     *
     * On remonte donc du point touché jusqu'au panneau : si l'on croise un
     * défileur qui a **encore de la matière au-dessus de lui**, le geste ne
     * nous appartient pas.
     */
    const dansUnDefileurImbrique = (cible: EventTarget | null): boolean => {
      let noeud = cible instanceof Element ? cible : null;
      while (noeud !== null && noeud !== panel) {
        const style = getComputedStyle(noeud);
        const defile = style.overflowY === 'auto' || style.overflowY === 'scroll';
        if (defile && noeud.scrollHeight > noeud.clientHeight) return true;
        noeud = noeud.parentElement;
      }
      return false;
    };

    const debut = (event: TouchEvent) => {
      const touche = event.touches[0];
      if (touche === undefined || event.touches.length > 1) {
        geste.current = null;
        return;
      }
      if (dansUnDefileurImbrique(event.target)) {
        geste.current = null;
        return;
      }
      geste.current = {
        id: touche.identifier,
        x: touche.clientX,
        y: touche.clientY,
        tient: false,
      };
    };

    const bouge = (event: TouchEvent) => {
      const g = geste.current;
      if (g === null) return;
      const touche = [...event.touches].find((t) => t.identifier === g.id);
      if (touche === undefined) return;

      const dx = touche.clientX - g.x;
      const dy = touche.clientY - g.y;

      /*
       * ⚠️ **On ne prend la main qu'au sommet, et seulement vers le bas.**
       * Tant que le panneau a de quoi défiler au-dessus, le geste lui
       * appartient — c'est ce qui fait que le tirage *prolonge* le défilement
       * au lieu de le remplacer.
       *
       * 🔴 **Et il faut un VERROU D'AXE, sans quoi un graphique se ferme sous
       * les doigts.** `ExerciseChart` vit dans une feuille : un balayage
       * horizontal de sa courbe qui dérive de trois pixels vers le bas
       * suffirait à déclencher la fermeture, alors qu'on lisait ses points.
       * On exige donc un geste franchement vertical — le même seuil de 10 px
       * que le pager, le glissement de suppression et le balayage des
       * graphiques, « deux seuils différents sur un même écran se sentent au
       * doigt ».
       *
       * ⚠️ Et une fois pris, on ne rend plus la main (`tient`) : sans ça, un
       * doigt qui remonte de quelques pixels au milieu du tirage rendrait le
       * geste au navigateur, et la feuille sauterait.
       */
      if (!g.tient) {
        if (dy <= SHEET_PULL_LOCK_PX || Math.abs(dx) >= dy) return;
        if (panel.scrollTop > 0) return;
        g.tient = true;
      }
      if (event.cancelable) event.preventDefault();
      /*
       * ⚠️ **Le seuil du verrou est RETRANCHÉ**, sinon la feuille sauterait de
       * 10 px à l'instant précis où elle prend la main — elle doit naître sous
       * le doigt, pas apparaître décalée. Le seuil de fermeture se compte donc
       * à partir de là, ce qui fait 120 px de course réelle : c'est la même
       * distance qu'à la poignée, plus le prix du verrou.
       */
      onPull(Math.max(0, dy - SHEET_PULL_LOCK_PX));
    };

    const fin = () => {
      if (geste.current?.tient === true) onRelease();
      geste.current = null;
    };

    panel.addEventListener('touchstart', debut, { passive: true });
    panel.addEventListener('touchmove', bouge, { passive: false });
    panel.addEventListener('touchend', fin);
    panel.addEventListener('touchcancel', fin);
    return () => {
      panel.removeEventListener('touchstart', debut);
      panel.removeEventListener('touchmove', bouge);
      panel.removeEventListener('touchend', fin);
      panel.removeEventListener('touchcancel', fin);
    };
  }, [panel, onPull, onRelease]);
}

export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const [drag, setDrag] = useState(0);
  /*
   * Le panneau, en **état** : c'est lui le conteneur de défilement, et le
   * tirage a besoin de son `scrollTop`. Cf. `useSheetPull`.
   */
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  // `dragging` est un *état* et non une ref : le rendu s'en sert pour couper la
  // transition, et lire une ref pendant le rendu ne garantirait pas un nouveau
  // rendu au bon moment.
  const [dragging, setDragging] = useState(false);
  const origin = useRef<number | null>(null);

  useHideChromeWhileOpen(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  /*
   * ⚠️ **Le tirage depuis le contenu partage le SEUIL et la remise à zéro du
   * glissement depuis la poignée.** Deux distances de fermeture sur la même
   * feuille se sentiraient au doigt — c'est la règle déjà tenue entre le pager
   * et le glissement de suppression (« deux seuils différents sur un même écran
   * se sentent au doigt »).
   */
  const tirer = useCallback((dy: number) => {
    setDragging(true);
    setDrag(dy);
  }, []);

  const relacher = useCallback(() => {
    setDragging(false);
    setDrag((distance) => {
      if (distance > SHEET_DISMISS_PX) onClose();
      return 0;
    });
  }, [onClose]);

  useSheetPull(panel, tirer, relacher);

  /*
   * 🔴 **Tous les hooks au-dessus de ce retour anticipé.** `useSheetPull` et
   * ses deux rappels étaient posés plus bas au premier jet : React aurait
   * refusé des hooks conditionnels, et le typecheck ne voit rien — c'est le
   * lint qui l'a dit. Même défaut que sur les graphiques le même jour.
   */
  if (!open) return null;

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    /*
     * 🔴 Un geste qui part d'un **contrôle** n'est pas un glissement de feuille.
     *
     * Sans ce garde-fou, `setPointerCapture` ci-dessous retargette non seulement
     * les événements pointeur mais aussi les événements de compatibilité —
     * `mouseup` et surtout **`click`** — sur l'élément qui capture. Le bouton
     * « Fermer », enfant de l'en-tête, recevait donc son `pointerdown` puis plus
     * rien : le `click` partait sur l'en-tête. Conséquence : **le bouton
     * « Fermer » de toutes les feuilles modales était inerte depuis l'étape 5**,
     * date d'ajout du glissement. Le défaut ne s'est pas vu parce que les deux
     * autres façons de fermer — glisser vers le bas, toucher le voile —
     * fonctionnent, et qu'on les utilise naturellement sur iPhone.
     *
     * Constaté au banc en instrumentant les événements, pas au typecheck.
     */
    if (
      event.target instanceof Element &&
      event.target.closest('button, a, input, select, textarea')
    ) {
      return;
    }
    origin.current = event.clientY;
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (origin.current === null) return;
    // Vers le haut : rien. Une feuille ne se tire pas au-delà de sa position.
    setDrag(Math.max(0, event.clientY - origin.current));
  };

  const endDrag = () => {
    if (origin.current === null) return;
    origin.current = null;
    setDragging(false);
    if (drag > SHEET_DISMISS_PX) onClose();
    setDrag(0);
  };


  const dragHandlers = {
    onPointerDown: startDrag,
    onPointerMove: moveDrag,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    // Le navigateur ne doit pas interpréter le geste comme un défilement.
    style: { touchAction: 'none' as const },
  };

  /*
   * 🔴 **Portail vers `document.body` — sans lui, une feuille ouverte depuis un
   * élément transformé s'affiche hors écran.**
   *
   * `position: fixed` ne se résout pas contre la fenêtre dès qu'un ancêtre porte
   * une `transform`, un `filter` ou même un simple `will-change: transform` :
   * cet ancêtre devient le bloc conteneur. Or la piste du pager (`.day-track`)
   * porte les deux — c'est ce qui fait glisser les journées.
   *
   * Conséquence mesurée au banc lorsque le menu « ⋯ » d'un repas est passé en
   * feuille : le panneau se posait à y = 885 px dans une fenêtre de 852 px,
   * donc **entièrement sous l'écran**, et aucune de ses options n'était
   * atteignable. Le composant était juste, son emplacement dans l'arbre ne
   * l'était pas.
   *
   * ⚠️ Un portail **ne déplace pas** l'arbre React : le contexte et la
   * remontée des événements suivent toujours le composant parent. Seul le nœud
   * DOM change de place.
   */
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="sheet-scrim"
      onClick={onClose}
    >
      <div
        className="sheet-panel"
        ref={setPanel}
        onClick={(event) => {
          event.stopPropagation();
        }}
        style={{
          transform: `translateY(${String(drag)}px)`,
          /*
           * Pas de transition pendant le geste : la feuille doit coller au
           * doigt. Elle ne revient en douceur qu'au relâchement.
           *
           * ⚠️ Et pendant le geste, l'animation d'entrée est **coupée** : sans
           * cela `sheet-in` continuerait de piloter la même `transform` que le
           * doigt, et la feuille sauterait au premier pixel de glissement.
           */
          transition: dragging ? 'none' : 'transform var(--t-base) var(--ease-out)',
          animation: drag > 0 ? 'none' : undefined,
        }}
      >
        {/* Poignée — affordance iOS, et zone de préhension du geste. */}
        <div {...dragHandlers} aria-hidden className="n-sheet__grab-zone">
          <div className="n-sheet__grab" />
        </div>

        <div {...dragHandlers} className="n-sheet__head">
          <h2 className="n-sheet__title">{title}</h2>
          <NourriButton variant="secondary" onClick={onClose} aria-label="Fermer">
            Fermer
          </NourriButton>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
